import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ThemeService } from '../services/themeService';

type AppMode = 'shop' | 'job' | 'edu' | 'med';

interface AppModeContextType {
  mode: AppMode;
  is_job: boolean;
  setMode: (mode: AppMode) => void;
  toggleMode: () => void;
}

const AppModeContext = createContext<AppModeContextType | undefined>(undefined);

interface AppModeProviderProps {
  children: ReactNode;
}

export const AppModeProvider: React.FC<AppModeProviderProps> = ({ children }) => {
  const [mode, setModeState] = useState<AppMode>('shop');

  // Derived boolean value for easy access
  const is_job = mode === 'job';

  const setMode = (newMode: AppMode) => {
    setModeState(newMode);
  };

  const toggleMode = () => {
    if (mode === 'shop') {
      setMode('job');
    } else if (mode === 'job') {
      setMode('edu');
    } else if (mode === 'edu') {
      setMode('med');
    } else {
      setMode('shop');
    }
  };

  // Sync theme with mode: job -> ocean, shop -> purple-blue
  useEffect(() => {
    const desiredTheme = is_job ? 'ocean-gradient' : 'purple-blue-gradient';
    // Do NOT override a user-selected dashboard theme.
    // Only set a mode-based theme if no theme has ever been chosen/stored.
    try {
      const stored = typeof window !== 'undefined' ? window.localStorage.getItem('dashboard_theme') : null;
      if (stored) return;
    } catch {
      // ignore
    }

    const current = ThemeService.getCurrentTheme();
    if (current !== desiredTheme) {
      ThemeService.setTheme(desiredTheme);
    }
  }, [is_job]);

  useEffect(() => {
    fetch('/website-settings.json', { cache: 'no-store' })
      .then((res) => res.ok ? res.json() : null)
      .then((data) => {
        if (data && (data.mode === 'shop' || data.mode === 'job' || data.mode === 'edu' || data.mode === 'med')) {
          setMode(data.mode);
        }
      })
      .catch(() => {});
  }, []);

  return (
    <AppModeContext.Provider value={{ mode, is_job, setMode, toggleMode }}>
      {children}
    </AppModeContext.Provider>
  );
};

export const useAppMode = (): AppModeContextType => {
  const context = useContext(AppModeContext);
  if (context === undefined) {
    throw new Error('useAppMode must be used within an AppModeProvider');
  }
  return context;
};
